import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Btn, Modal, Input, Textarea, Select, Toggle, ImageField, Field, labelCls, inputCls } from "./AdminUI";
import { Plus, Edit2, Trash2, Star, GripVertical, X } from "lucide-react";

const CATEGORIES = ["Residential", "Commercial", "Interior", "Renovation"];
const TAGS = ["Elevation", "Interior", "Floor Plan", "3D Floor Plan", "Construction", "Completed Projects", "Renovation", "Residential", "Commercial"];
const IMG_CATS = ["Exterior Elevation", "3D Elevation", "Floor Plan", "3D Floor Plan", "Interior", "Construction", "Site Progress", "Completed Project", "Before", "After"];
const STATUS = ["Completed", "Ongoing", "Planning"];
const AREA_UNITS = ["Sq.ft", "Sq.m", "Acres"];
const FLOORS_OPTS = ["Ground Floor", "G+1", "G+2", "G+3", "G+4", "2 Floors", "3 Floors", "4 Floors"];
const VIS_FIELDS = [
  ["location", "Location"], ["area", "Area"], ["plot_size", "Plot Size"], ["built_up_area", "Built-up Area"],
  ["floors", "Floors"], ["facing", "Facing"], ["bhk", "BHK"], ["service", "Service"], ["category", "Category"],
  ["status", "Status"], ["completion_year", "Year"], ["architecture_style", "Style"], ["client_name", "Client"],
];

const EMPTY = { name: "", client_name: "", location: "", category: "Residential", tags: [], service: "", property_type: "", plot_size: "", plot_width: "", plot_depth: "", facing: "", floors: "", bhk: "", built_up_area: "", area: "", area_unit: "Sq.ft", architecture_style: "", status: "Completed", completion_year: "", description: "", design_concept: "", design_concept_align: "left", overview: "", overview_align: "left", cover_image: "", images: [], custom_specs: [], field_visibility: {}, featured: false, homepage: true, published: true, order: 0, seo_title: "", seo_description: "" };

export default function ProjectsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/projects/admin/all").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name.trim()) return alert("Project name is required");
    const body = { ...editing };
    if (editing.id) await api.put(`/projects/admin/${editing.id}`, body);
    else await api.post("/projects/admin", body);
    setEditing(null); load();
  };
  const del = async (id) => { if (window.confirm("Delete this project?")) { await api.delete(`/projects/admin/${id}`); load(); } };

  const setF = (k, v) => setEditing({ ...editing, [k]: v });
  const toggleTag = (t) => setF("tags", editing.tags.includes(t) ? editing.tags.filter((x) => x !== t) : [...editing.tags, t]);
  const addImage = () => setF("images", [...editing.images, { url: "", category: "Exterior Elevation", alt: "", order: editing.images.length }]);
  const setImage = (i, k, v) => { const imgs = [...editing.images]; imgs[i] = { ...imgs[i], [k]: v }; setF("images", imgs); };
  const removeImage = (i) => setF("images", editing.images.filter((_, k) => k !== i));
  const setV = (key, val) => setF("field_visibility", { ...(editing.field_visibility || {}), [key]: val });
  const isVis = (key) => (editing.field_visibility || {})[key] !== false;
  const addSpec = () => setF("custom_specs", [...(editing.custom_specs || []), { label: "", value: "" }]);
  const setSpec = (i, k, v) => { const s = [...(editing.custom_specs || [])]; s[i] = { ...s[i], [k]: v }; setF("custom_specs", s); };
  const removeSpec = (i) => setF("custom_specs", (editing.custom_specs || []).filter((_, k) => k !== i));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-ink">Projects <span className="text-muted text-base">({items.length})</span></h1>
        <Btn onClick={() => setEditing({ ...EMPTY })} data-testid="add-project-btn"><Plus size={16} /> Add Project</Btn>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} className="rounded-xl2 bg-white border border-line overflow-hidden" data-testid={`admin-project-${p.slug}`}>
            <div className="relative aspect-video bg-sand">
              {p.cover_image && <img src={p.cover_image} alt={p.name} className="img-cover" />}
              {p.featured && <span className="absolute top-2 left-2 bg-gold text-white text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1"><Star size={10} /> Featured</span>}
              {!p.published && <span className="absolute top-2 right-2 bg-ink/80 text-white text-[10px] px-2 py-1 rounded-full">Draft</span>}
            </div>
            <div className="p-4">
              <h3 className="font-bold text-ink text-sm truncate">{p.name}</h3>
              <p className="text-xs text-muted">{p.category} · {p.location}</p>
              <div className="flex gap-2 mt-3">
                <Btn variant="outline" onClick={() => setEditing({ ...EMPTY, ...p })} data-testid={`edit-project-${p.slug}`}><Edit2 size={14} /> Edit</Btn>
                <Btn variant="danger" onClick={() => del(p.id)} data-testid={`delete-project-${p.slug}`}><Trash2 size={14} /></Btn>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal wide title={editing.id ? "Edit Project" : "Add Project"} onClose={() => setEditing(null)}>
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Project Name *" value={editing.name} onChange={(v) => setF("name", v)} />
            <Input label="Client Name" value={editing.client_name} onChange={(v) => setF("client_name", v)} />
            <Input label="Location" value={editing.location} onChange={(v) => setF("location", v)} />
            <Select label="Category" value={editing.category} onChange={(v) => setF("category", v)} options={CATEGORIES} />
            <Input label="Service" value={editing.service} onChange={(v) => setF("service", v)} />
            <Input label="Property Type" value={editing.property_type} onChange={(v) => setF("property_type", v)} />
            <Input label="Plot Size" value={editing.plot_size} onChange={(v) => setF("plot_size", v)} />
            <Input label="Plot Width (ft)" value={editing.plot_width} onChange={(v) => setF("plot_width", v)} />
            <Input label="Plot Depth (ft)" value={editing.plot_depth} onChange={(v) => setF("plot_depth", v)} />
            <Input label="Facing" value={editing.facing} onChange={(v) => setF("facing", v)} />
            <div>
              <label className={labelCls}>Floors</label>
              <input className={inputCls} list="floors-opts" value={editing.floors} onChange={(e) => setF("floors", e.target.value)} placeholder="e.g. G+1 (or type custom)" data-testid="project-floors" />
              <datalist id="floors-opts">{FLOORS_OPTS.map((f) => <option key={f} value={f} />)}</datalist>
            </div>
            <Input label="BHK" value={editing.bhk} onChange={(v) => setF("bhk", v)} />
            <div>
              <label className={labelCls}>Area</label>
              <div className="flex gap-2">
                <input className={inputCls} value={editing.area} onChange={(e) => setF("area", e.target.value)} placeholder="e.g. 5500" data-testid="project-area" />
                <input className={inputCls + " w-28"} list="area-units" value={editing.area_unit} onChange={(e) => setF("area_unit", e.target.value)} placeholder="Sq.ft" data-testid="project-area-unit" />
                <datalist id="area-units">{AREA_UNITS.map((u) => <option key={u} value={u} />)}</datalist>
              </div>
            </div>
            <Input label="Built-up Area" value={editing.built_up_area} onChange={(v) => setF("built_up_area", v)} />
            <Input label="Architecture Style" value={editing.architecture_style} onChange={(v) => setF("architecture_style", v)} />
            <Select label="Status" value={editing.status} onChange={(v) => setF("status", v)} options={STATUS} />
            <Input label="Completion Year" value={editing.completion_year} onChange={(v) => setF("completion_year", v)} />
            <Input label="Display Order" type="number" value={editing.order} onChange={(v) => setF("order", parseInt(v) || 0)} />
          </div>

          <div className="mt-4">
            <label className={labelCls}>Filter Tags (shown in homepage/projects filters)</label>
            <div className="flex flex-wrap gap-2">
              {TAGS.map((t) => (
                <button key={t} type="button" onClick={() => toggleTag(t)} className={`text-xs px-3 py-1.5 rounded-full border ${editing.tags.includes(t) ? "bg-gold text-white border-gold" : "border-line text-ink"}`}>{t}</button>
              ))}
            </div>
          </div>

          <div className="grid gap-4 mt-4">
            <Textarea label="Short Description" value={editing.description} onChange={(v) => setF("description", v)} />
            <Textarea label="Overview" value={editing.overview} onChange={(v) => setF("overview", v)} align={editing.overview_align} onAlign={(v) => setF("overview_align", v)} />
            <Textarea label="Design Concept" value={editing.design_concept} onChange={(v) => setF("design_concept", v)} align={editing.design_concept_align} onAlign={(v) => setF("design_concept_align", v)} />
          </div>

          <div className="mt-4"><ImageField label="Cover Image" value={editing.cover_image} onChange={(v) => setF("cover_image", v)} folder="gnons/projects/" /></div>

          <div className="mt-5">
            <div className="flex items-center justify-between mb-2">
              <label className={labelCls + " mb-0"}>Project Images ({editing.images.length})</label>
              <Btn variant="outline" onClick={addImage} data-testid="add-image-btn"><Plus size={14} /> Add Image</Btn>
            </div>
            <div className="space-y-3">
              {editing.images.map((im, i) => (
                <div key={i} className="rounded-lg border border-line p-3 bg-sand">
                  <div className="flex items-start gap-3">
                    <div className="relative w-16 h-16 rounded border border-line overflow-hidden bg-white shrink-0">{im.url && <img src={im.url} alt="" className="img-cover" />}</div>
                    <div className="flex-1 grid sm:grid-cols-2 gap-2">
                      <input className={inputCls} placeholder="Image URL" value={im.url} onChange={(e) => setImage(i, "url", e.target.value)} />
                      <select className={inputCls} value={im.category} onChange={(e) => setImage(i, "category", e.target.value)}>{IMG_CATS.map((c) => <option key={c}>{c}</option>)}</select>
                      <input className={inputCls + " sm:col-span-2"} placeholder="Alt text" value={im.alt} onChange={(e) => setImage(i, "alt", e.target.value)} />
                    </div>
                    <div className="flex flex-col gap-1">
                      <button type="button" onClick={() => setF("cover_image", im.url)} title="Set as cover" className="text-gold p-1"><Star size={16} /></button>
                      <button type="button" onClick={() => removeImage(i)} className="text-red-500 p-1"><X size={16} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <label className={labelCls + " mb-0"}>Custom Specifications</label>
              <Btn variant="outline" onClick={addSpec} data-testid="add-spec-btn"><Plus size={14} /> Add Spec</Btn>
            </div>
            <p className="text-xs text-muted mb-2">Reusable Label → Value pairs (e.g. Parking → 2 Cars, Road Width → 30 ft). Empty rows are ignored.</p>
            <div className="space-y-2">
              {(editing.custom_specs || []).map((s, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`spec-row-${i}`}>
                  <input className={inputCls} placeholder="Label (e.g. Parking)" value={s.label} onChange={(e) => setSpec(i, "label", e.target.value)} />
                  <span className="text-muted">→</span>
                  <input className={inputCls} placeholder="Value (e.g. 2 Cars)" value={s.value} onChange={(e) => setSpec(i, "value", e.target.value)} />
                  <button type="button" onClick={() => removeSpec(i)} className="text-red-500 p-1 shrink-0"><X size={16} /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <label className={labelCls}>Show on Website (toggle which specifications appear publicly)</label>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 rounded-lg border border-line p-3 bg-sand">
              {VIS_FIELDS.map(([key, label]) => (
                <label key={key} className="flex items-center justify-between text-sm py-1" data-testid={`vis-${key}`}>
                  <span className="text-ink">{label}</span>
                  <input type="checkbox" checked={isVis(key)} onChange={(e) => setV(key, e.target.checked)} className="w-4 h-4 accent-[#C88A32]" />
                </label>
              ))}
            </div>
            <p className="text-xs text-muted mt-1">Empty values are never shown, even if toggled on.</p>
          </div>

          <div className="grid sm:grid-cols-3 gap-3 mt-5">
            <Toggle label="Featured" checked={editing.featured} onChange={(v) => setF("featured", v)} />
            <Toggle label="Show on Homepage" checked={editing.homepage} onChange={(v) => setF("homepage", v)} />
            <Toggle label="Published" checked={editing.published} onChange={(v) => setF("published", v)} />
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <Input label="SEO Title" value={editing.seo_title} onChange={(v) => setF("seo_title", v)} />
            <Input label="SEO Description" value={editing.seo_description} onChange={(v) => setF("seo_description", v)} />
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Btn variant="outline" onClick={() => setEditing(null)}>Cancel</Btn>
            <Btn onClick={save} data-testid="save-project-btn">Save Project</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
